import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, CalendarClock, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { useData } from "@/context/DataContext";
import TaskFormDialog from "@/components/dialogs/TaskFormDialog";
import ConfirmDialog from "@/components/dialogs/ConfirmDialog";
import { toast } from "sonner";

export default function Tasks() {
  const { tasks, clients, addTask, updateTask, deleteTask } = useData();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setCreateOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const toggle = (id, done) => updateTask(id, { done });
  const clientOptions = clients.map((c) => c.name);

  const handleCreate = (form) => {
    addTask(form);
    toast.success("Task created");
  };
  const handleEdit = (form) => {
    updateTask(editTarget.id, form);
    setEditTarget(null);
    toast.success("Task updated");
  };
  const handleDelete = () => {
    deleteTask(deleteTarget.id);
    setDeleteTarget(null);
    toast.success("Task deleted");
  };

  return (
    <div className="space-y-8" data-testid="tasks-page">
      <PageHeader
        title="Task Manager"
        subtitle="Stay on top of your daily operations with a clean, organized task list."
        actions={
          <Button
            data-testid="create-task-button"
            onClick={() => setCreateOpen(true)}
            className="bg-primary hover:bg-primary/90 rounded-lg btn-premium"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Task
          </Button>
        }
      />

      <Card className="border border-slate-200 dark:border-slate-800 rounded-xl elev-1 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">
            {tasks.filter((t) => !t.done).length} open · {tasks.filter((t) => t.done).length} done
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{tasks.length} total</div>
        </div>
        <ul className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="task-list">
          {tasks.map((t) => (
            <li
              key={t.id}
              data-testid={`task-item-${t.id}`}
              className="px-6 py-4 flex items-center gap-4 hover:bg-slate-50 dark:hover:bg-slate-800/60 dark:bg-slate-900/80 dark:bg-slate-900/40 transition-colors"
            >
              <Checkbox
                checked={!!t.done}
                onCheckedChange={(v) => toggle(t.id, !!v)}
                data-testid={`task-checkbox-${t.id}`}
                className="rounded-md"
              />
              <div className="flex-1 min-w-0">
                <div
                  className={`text-sm font-medium ${
                    t.done ? "text-slate-400 dark:text-slate-500 line-through" : "text-slate-900 dark:text-slate-100"
                  }`}
                >
                  {t.title}
                </div>
                <div className="mt-1 flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                  <span className="flex items-center gap-1">
                    <CalendarClock className="h-3.5 w-3.5" />
                    {t.due}
                  </span>
                  <span className="text-slate-300 dark:text-slate-600">·</span>
                  <span>{t.client || "Unassigned"}</span>
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-2 shrink-0">
                <StatusBadge status={t.priority} />
                <StatusBadge status={t.status} />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid={`task-menu-${t.id}`}
                    className="h-8 w-8 text-slate-400 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white dark:text-slate-100"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    data-testid={`task-edit-${t.id}`}
                    onClick={() => setEditTarget(t)}
                  >
                    <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid={`task-delete-${t.id}`}
                    onClick={() => setDeleteTarget(t)}
                    className="text-red-600 dark:text-red-400 focus:text-red-600 dark:text-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
          ))}
        </ul>
      </Card>

      <TaskFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreate}
        clientOptions={clientOptions}
      />
      <TaskFormDialog
        open={!!editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
        onSubmit={handleEdit}
        initial={editTarget}
        clientOptions={clientOptions}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete task?"
        description={deleteTarget ? `"${deleteTarget.title}" will be removed.` : ""}
        onConfirm={handleDelete}
        testid="delete-task-confirm"
      />
    </div>
  );
}
